import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeLatest } from 'redux-saga/effects';

import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload
} from '@ps20-frontend/shop-integration-redux';
import { Feature, OutbuildingSizeCategory, VorschadenArt } from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { convertRisikoortToBffAdresse } from '@ps20-frontend/vorschlagsdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { VwgV2DatenAntrag } from '@ps20/bff-models/dist/lib/ps20-bff-model-antrag';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import { selectExtremwetterschutzActiveValue } from '../../app/shared/selectors/elementar-selbstbehalt.selector';
import { isNebengebaeudeLeistungSelectable } from '../../app/shared/selectors/gebaeude.selector';
import { selectIsErsatzgeschaeft } from '../../app/shared/selectors/vertragsoption.selector';
import { resolveOutdoorPool } from './transformer/vwgData.transformer';

const isVersicherteNebengebaeudeDeselected = (state: IAppState): boolean => {
  const leistungen = state.produkt?.produktlinien?.[0]?.leistungen;
  const versicherteNebengebaeude = leistungen?.find(l => l.id === 'versicherteNebengebaeude');
  return versicherteNebengebaeude?.abgewaehlt === true;
};

const isOutbuildingDataActive = (state: IAppState): boolean =>
  state.gebaeude.nebengebaeude.available === true &&
  !isVersicherteNebengebaeudeDeselected(state) &&
  isNebengebaeudeLeistungSelectable(state);

const getNebengebaeudeQM = (state: IAppState): number | null =>
  isOutbuildingDataActive(state) ? state.gebaeude.nebengebaeude.totalArea : null;

const getOutbuildingSizeCategory = (state: IAppState): OutbuildingSizeCategory | null =>
  isOutbuildingDataActive(state) ? state.gebaeude.nebengebaeude.sizeCategory : null;

export function* watchVorschlagdatenChanged(): SagaIterator {
  yield takeLatest(
    GlobalActions.VORSCHLAGDATEN_REQUESTED,
    safeSaga(vorschlagdatenSaga, App.VWG_V2)
  );
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  try {
    const { angebotsIds } = action.payload;
    const { angebotsId }: IAppState = yield select();

    if (!angebotsIds.some(angebot => angebot.appId === App.VWG_V2 && angebot.angebotsId === angebotsId)) {
      return yield cancel();
    }
    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
    const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

    const vorschlagdaten: VwgV2DatenAntrag = {
      spartenType: state.produkt.spartenType,
      angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
      angebotsId: state.angebotsId,
      jahresbeitrag: state.jahresbeitrag,
      savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
      gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.VWG_V2,
      ),
      savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
      antragsId: state.antragsnummer,
      sicherungsglaeubiger: null,
      hspLfnr: state.produkt.hspLfnr,
      gebaeude: {
        ferienhaus: state.buildingFeatures.vacationHome,
        holzhaus: state.buildingFeatures.woodenHouse,
        dachbelagWeich: state.buildingFeatures.softRoofCovering,
        flachdach: state.buildingFeatures.flatRoof,
        adresse: convertRisikoortToBffAdresse(state.risikoort),
        baujahr: state.gebaeude.baujahr,
        wohnflaecheQM: state.gebaeude.wohnflaeche,
        nebengebaeudeQM: getNebengebaeudeQM(state),
        kellerVorhanden: state.buildingFeatures.basement,
        kellerMitWohnflaeche: state.buildingFeatures.basementWithLivingSpace ?? false,
        leerstehend: state.buildingFeatures.notPermanentlyOccupied,
        schwimmbad: state.buildingFeatures.pool,
        solarthermie: state.buildingFeatures.solarThermal,
        photovoltaik: state.buildingFeatures.photovoltaic,
        geothermie: state.buildingFeatures.geothermal,
        denkmalschutz: state.buildingFeatures.heritageProtection,
        feuerrohbau: state.buildingFeatures.fireShellConstruction,
        vorschaedenVorhanden: state.vorschaeden.vorhanden,
        vorschaeden: getVorschaeden(state),
        gebkategorie: '',
        gebklasse: state.buildingFeatures.buildingClass,
        buildingFeatures: {
          rented: state.buildingFeatures.rented,
          indoorPool: state.buildingFeatures.poolIndoors,
          outdoorPool: resolveOutdoorPool(state),
          heatPump: state.buildingFeatures.geothermal,
          gasHeating: state.buildingFeatures.gasHeating,
          oilHeating: state.buildingFeatures.oilHeating,
          pelletHeating: state.buildingFeatures.pelletHeating,
          districtHeating: state.buildingFeatures.districtHeating,
        },
        outbuildingSizeCategory: getOutbuildingSizeCategory(state),
      },
      produkt: {
        beginn: null,
        ablauf: null,
        selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
        produktline: ausgewaehlteProduktlinie.produktName,
        kurztext: ausgewaehlteProduktlinie.kurztext,
        langtext: ausgewaehlteProduktlinie.langtext,
        fachschluessel: getFachschluessel(state.produkt),
      },
      rbdMax: ausgewaehlteProduktlinie.rbdMax,
      rbdAktuell: state.taasInfo.rbdNachlass,
      treuebonus: state.taasInfo.tbNachlass,
      savedRbdNachlass: state.taasInfo.savedRbdNachlass,
      savedTbNachlass: state.taasInfo.savedTbNachlass,
      vorteilsrabatt: state.taasInfo.vorteilsrabatt,
      preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
      vorversicherung: [],
      antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
      laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
      rabattstufe: state.rabattstufe,
      geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
      vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
      vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
      zielmarktAbweichung: false,
      kooperation: state.vertragsoptionen.selectedKooperation,
      elementarfragebogenFileToAnalyzeAvailable: false,
      extremwetterschutzSelbstbeteiligung: selectExtremwetterschutzActiveValue(state),
      zuersZone: state.gisDaten?.zuersGefaehrdungsklasse,
    };

    handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten)
    );
  } catch (e) {
    PS20Logger.error(App.VWG_V2, e as Error, 'VorschlagdatenSaga.vorschlagdatenSaga()');
  }
}

function getVorschaeden(state: IAppState) {
  const vorschadenAntrag = [
    {
      vorschadenArt: VorschadenArt.ELEMENTAR,
      anzahl: state.vorschaeden.elementarVorschaden.includeInEinreichen
        ? state.vorschaeden.elementarVorschaden.count
        : 0,
      betrachtungszeitraum: 5,
    },
    {
      vorschadenArt: VorschadenArt.FEUER,
      anzahl: state.vorschaeden.feuerVorschaden.includeInEinreichen
        ? state.vorschaeden.feuerVorschaden.count
        : 0,
      betrachtungszeitraum: 5,
    },
    {
      vorschadenArt: VorschadenArt.LEITUNGSWASSER,
      anzahl: state.vorschaeden.leitungswasserVorschaden.includeInEinreichen
        ? state.vorschaeden.leitungswasserVorschaden.count
        : 0,
      betrachtungszeitraum: 5,
    },
    {
      vorschadenArt: VorschadenArt.STURM_HAGEL,
      anzahl: state.vorschaeden.sturmhagelVorschaden.includeInEinreichen
        ? state.vorschaeden.sturmhagelVorschaden.count
        : 0,
      betrachtungszeitraum: 5,
    },
  ];
  const visibleInErsatzgeschaeftByArt = new Map<VorschadenArt, boolean>([
    [VorschadenArt.ELEMENTAR, state.vorschaeden.elementarVorschaden.visibleInErsatzgeschaeft],
    [VorschadenArt.FEUER, state.vorschaeden.feuerVorschaden.visibleInErsatzgeschaeft],
    [VorschadenArt.LEITUNGSWASSER, state.vorschaeden.leitungswasserVorschaden.visibleInErsatzgeschaeft],
    [VorschadenArt.STURM_HAGEL, state.vorschaeden.sturmhagelVorschaden.visibleInErsatzgeschaeft],
  ]);
  return selectIsErsatzgeschaeft(state)
    ? vorschadenAntrag.filter((value) => visibleInErsatzgeschaeftByArt.get(value.vorschadenArt))
    : vorschadenAntrag;
}
